import { createFileRoute, Link } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { Trash2, ShoppingBag, ArrowRight } from 'lucide-react';
import { Button, QuantitySelector } from '@kamalkari/ui';
import { formatPrice } from '@kamalkari/utils';
import { useCartStore } from '@/stores/cartStore';
import { ShippingCalculator } from '@/components/ShippingCalculator';

export const Route = createFileRoute('/cart')({
  component: CartPage,
});

function CartPage() {
  const { items, removeItem, updateQuantity, getTotalPrice } = useCartStore();

  if (items.length === 0) {
    return (
      <main className="container-page py-20">
        <div className="max-w-md mx-auto text-center">
          <ShoppingBag className="w-16 h-16 mx-auto text-warm-300 mb-6" />
          <h1 className="text-2xl font-serif text-warm-900 mb-4">Your cart is empty</h1>
          <p className="text-warm-600 mb-8">
            Discover our collection of authentic Kalamkari art and find something special.
          </p>
          <Button asChild>
            <Link to="/products">Continue Shopping</Link>
          </Button>
        </div>
      </main>
    );
  }

  return (
    <main className="container-page py-12">
      <h1 className="text-3xl font-serif text-warm-900 mb-8">Shopping Cart</h1>

      <div className="grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2">
          <div className="space-y-6">
            {items.map((item, index) => (
              <motion.div
                key={`${item.productId}-${item.variantId || ''}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex gap-6 p-4 bg-white rounded-lg border border-warm-200"
              >
                <div className="w-24 h-32 rounded-sm overflow-hidden bg-warm-100 flex-shrink-0">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-full h-full object-cover"
                  />
                </div>

                <div className="flex-1 min-w-0">
                  <h3 className="font-serif text-lg text-warm-900 mb-1">{item.name}</h3>
                  <p className="text-sm text-warm-500 mb-4">SKU: {item.sku}</p>

                  <div className="flex items-center justify-between">
                    <QuantitySelector
                      value={item.quantity}
                      onChange={(qty) =>
                        updateQuantity(item.productId, qty, item.variantId)
                      }
                      max={10}
                    />

                    <div className="flex items-center gap-4">
                      <p className="font-medium text-warm-900">
                        {formatPrice(item.price * item.quantity)}
                      </p>
                      <button
                        onClick={() => removeItem(item.productId, item.variantId)}
                        className="p-2 text-warm-400 hover:text-madder transition-colors"
                        aria-label="Remove item"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-1">
          <div className="sticky top-24 space-y-6">
            <div className="bg-white rounded-lg border border-warm-200 p-6">
              <h2 className="font-serif text-xl text-warm-900 mb-6">Order Summary</h2>

              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-warm-600">
                  <span>Subtotal</span>
                  <span>{formatPrice(getTotalPrice())}</span>
                </div>
                <div className="flex justify-between text-warm-600">
                  <span>Shipping</span>
                  <span className="text-sm">Calculated at checkout</span>
                </div>
              </div>

              <div className="border-t border-warm-200 pt-4 mb-6">
                <div className="flex justify-between text-lg font-medium text-warm-900">
                  <span>Total</span>
                  <span>{formatPrice(getTotalPrice())}</span>
                </div>
              </div>

              <Button asChild className="w-full" size="lg">
                <Link to="/checkout">
                  Proceed to Checkout
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>

              <p className="text-xs text-warm-500 text-center mt-4">
                Tax calculated at checkout. Free shipping on orders over $100.
              </p>
            </div>

            <ShippingCalculator />
          </div>
        </div>
      </div>
    </main>
  );
}
